import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

// Mock user database
const mockUsers = [
  { id: 1, email: 'demo@letterly.com', password: 'password123', name: 'Demo User' },
  { id: 2, email: 'test@letterly.com', password: 'test123', name: 'Test User' },
  { id: 3, email: 'admin@letterly.com', password: 'admin123', name: 'Admin User' }
];

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in from localStorage
    const savedUser = localStorage.getItem('user');
    const savedAuth = localStorage.getItem('isAuthenticated');
    
    if (savedUser && savedAuth === 'true') {
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    try {
      const foundUser = mockUsers.find(u => u.email === email && u.password === password);
      
      if (foundUser) {
        const userInfo = { id: foundUser.id, email: foundUser.email, name: foundUser.name };
        setUser(userInfo);
        setIsAuthenticated(true);
        
        // Save to localStorage for persistence
        localStorage.setItem('user', JSON.stringify(userInfo));
        localStorage.setItem('isAuthenticated', 'true');
        
        return { success: true };
      } else {
        return { 
          success: false, 
          error: 'Invalid email or password' 
        };
      }
    } catch (error) {
      return { 
        success: false, 
        error: 'Login failed. Please try again.' 
      };
    }
  };

  const register = async (email, password) => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    try {
      // Check if user already exists
      const existingUser = mockUsers.find(u => u.email === email);
      if (existingUser) {
        return { 
          success: false, 
          error: 'User with this email already exists' 
        };
      }
      
      // Add new user to mock database (in real app, this would be persisted)
      const newUser = {
        id: mockUsers.length + 1,
        email,
        password,
        name: email.split('@')[0] // Use email prefix as name
      };
      mockUsers.push(newUser);
      
      return { success: true, data: { message: 'Account created successfully!' } };
    } catch (error) {
      return { 
        success: false, 
        error: 'Registration failed. Please try again.' 
      };
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    register,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
